import ufeApi from 'services/api/ufeApi';
import UUIDv4 from 'utils/UUID';
import Hashing from 'utils/Hashing';
import authenticationUtils from 'utils/Authentication';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import GenerateTokens from 'services/api/authentication/generateLithiumSsoToken';
import RCPSCookies from 'utils/RCPSCookies';
import { URLS, AUTH_HEADERS } from 'constants/authentication';
import { MFA_MODE } from 'constants/phoneVerification';
import { setAuthOperationInProgress } from 'services/auth/newFlow/authFlow';
import BeyondIdentityUtils from 'utils/BeyondIdentity';
import PasskeyService from 'services/PasskeyService';
import passkeysUtils from 'utils/passkeys';
import Actions from 'Actions';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import PasskeysActionsEN from 'actions/locales/PasskeysActions_en_US';
import PasskeysActionsFR from 'actions/locales/PasskeysActions_fr_CA';
import store from 'store/Store';

const { sha256 } = Hashing;
const { isAuthServiceEnabled, getFingerPrint } = authenticationUtils;
const { generateTokens } = GenerateTokens;
const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(PasskeysActionsEN, PasskeysActionsFR);

/**
 * Check if error is a revoked passkey error from BeyondIdentity
 * @param {Object} error - Error object
 * @returns {boolean}
 */
const isRevokedPasskeyError = error => {
    if (error?.message && /state is REVOKED/i.test(error.message)) {
        return true;
    }

    return false;
};

/**
 * Clean up all passkeys for the given email address
 * @param {string} email - User email
 */
const cleanupPasskeysByEmail = async email => {
    if (!email) {
        return;
    }

    // Don't attempt cleanup if passkeys disabled
    if (!passkeysUtils.isPasskeyEnabled()) {
        return;
    }

    try {
        const localPasskeys = await BeyondIdentityUtils.getPasskeys();

        if (!localPasskeys || localPasskeys.length === 0) {
            return;
        }

        // Delete all passkeys that match the email (in parallel)
        const deletePromises = localPasskeys
            .filter(pk => {
                const passkeyEmail = pk.identity?.emailAddress || pk.identity?.traits?.username;

                return passkeyEmail === email;
            })
            .map(async pk => {
                try {
                    await BeyondIdentityUtils.deletePasskey(pk.id);
                } catch (deleteError) {
                    Sephora.logger?.error('[Passkey] Failed to delete passkey for revoked credential', {
                        email,
                        passkeyId: pk.id,
                        error: deleteError?.message || deleteError
                    });
                }
            });

        await Promise.all(deletePromises);
    } catch (error) {
        Sephora.logger?.error('[Passkey] Failed to cleanup passkeys', {
            email,
            error: error?.message || error
        });
    }
};

/**
 * Handle passkey error - show error modal and reopen sign-in
 * @param {string} email - User email
 * @param {boolean} shouldCleanup - Whether to cleanup passkeys before showing modal
 */
const handlePasskeyError = async (email, shouldCleanup = false) => {
    // Clean up all passkeys for this email if requested
    if (shouldCleanup) {
        await cleanupPasskeysByEmail(email);
    }

    // Show error modal
    store.dispatch(
        Actions.showInfoModal({
            isOpen: true,
            title: getText('error'),
            message: getText('passkeyAuthenticationErrorMessage'),
            buttonText: getText('ok'),
            callback: () => {
                store.dispatch(Actions.showInfoModal({ isOpen: false }));
                store.dispatch(
                    Actions.showSignInModal({
                        isOpen: true,
                        email,
                        extraParams: {
                            skipPasskeyCheck: true
                        }
                    })
                );
            }
        })
    );
};

/**
 * Handle revoked passkey error - cleanup and show error modal
 * @param {string} email - User email
 */
const handleRevokedPasskeyError = async email => {
    await handlePasskeyError(email, true);
};

// https://jira.sephora.com/wiki/display/ILLUMINATE/Login+API

function login({ deviceFingerprint, login: log1n, password, options }) {
    // Set flag to skip auth flow during login
    // cookieBasedAuth not to trigger authFlow.execute during generateTokens call
    setAuthOperationInProgress(true);

    const {
        loginForCheckout, isKeepSignedIn, isOrderConfirmation, biAccountInfo, extraParams: allExtraParams
    } = options;
    const { isCheckoutInitAttempt, headerValue, ...extraParams } = allExtraParams || {};

    let body = {
        login: log1n,
        password,
        ...(isKeepSignedIn && deviceFingerprint && { deviceFingerprint })
    };

    if (isOrderConfirmation) {
        body.source = 'orderConfirmation';

        if (biAccountInfo) {
            body.isJoinBi = biAccountInfo.isJoinBi;
            body.biAccount = biAccountInfo.birthday;
            body.subscription = biAccountInfo.subscription;
        }
    } else {
        body.loginForCheckout = loginForCheckout;
        body.isKeepSignedIn = isKeepSignedIn;
    }

    if (Object.keys(extraParams).length) {
        body = {
            ...body,
            ...extraParams
        };
    }

    // Passkey flow: ensure the local device id is not sent to backend
    if (extraParams?.isPasskeyLogin && 'id' in body) {
        delete body.id;
    }

    if (isAuthServiceEnabled()) {
        body.email = body.login;
        delete body.login;

        body.keepSignedIn = body.isKeepSignedIn;
        delete body.isKeepSignedIn;

        const randomNumber = UUIDv4();

        return sha256(randomNumber).then(async function (digest) {
            const token = digest;
            body.randomNumber = token;
            let url = URLS.ATG_LOGIN_URL;
            const headers = AUTH_HEADERS;
            const xCausedHeader = headerValue || location.href;
            headers['X-CAUSED-BY-URL'] = xCausedHeader;

            if (RCPSCookies.isRCPSAuthEnabled()) {
                url = URLS.SDN_LOGIN_URL;
                headers.deviceId = await getFingerPrint();
            }

            const performLoginRequest = async () => {
                return ufeApi
                    .makeRequest(url, {
                        method: 'POST',
                        body: JSON.stringify(body),
                        headers
                    })
                    .then(async tokenData => {
                        if (tokenData?.errors) {
                            return Promise.reject(tokenData.errors[0]);
                        }

                        if (tokenData?.responseStatus >= 400) {
                            return Promise.reject(tokenData);
                        }

                        if (Sephora.configurationSettings?.enableStepUpAuthFlowBP2 && tokenData?.mfaEnabled) {
                            const mfaChallengeOptions = {};

                            if (tokenData?.mfaMode === MFA_MODE.PHONE) {
                                mfaChallengeOptions['shouldTriggerPhoneMFA'] = true;
                                mfaChallengeOptions['phoneLast4Digits'] = tokenData?.phoneLast4Digits;
                            }

                            tokenData?.mfaMode === MFA_MODE.EMAIL && (mfaChallengeOptions.shouldTriggerEmailMfa = true);

                            return Promise.resolve({
                                mfaEnabled: true,
                                ...mfaChallengeOptions
                            });
                        }

                        if (tokenData?.isStoreBiMember && Sephora.configurationSettings?.isEmailVerificationEnabled) {
                            return Promise.resolve({
                                shouldTriggerEmailVerification: true,
                                isStoreBiMember: true
                            });
                        }

                        if (tokenData?.profileWarnings) {
                            Storage.local.setItem(LOCAL_STORAGE.LOGIN_PROFILE_WARNINGS, tokenData?.profileWarnings);

                            if (isCheckoutInitAttempt) {
                                Storage.local.setItem(LOCAL_STORAGE.POSTPONE_LOGIN_PROFILE_WARNINGS, true);
                            }
                        }

                        body.token = tokenData.token;
                        body.randomNumber = randomNumber;

                        const generateTokensResponse = await generateTokens(body, { headerValue: xCausedHeader });

                        return {
                            ...generateTokensResponse,
                            hasIdentity: tokenData.hasIdentity
                        };
                    })
                    .catch(error => {
                        // Handle passkey login errors
                        if (extraParams?.isPasskeyLogin) {
                            setAuthOperationInProgress(false);

                            // NOTE: ERR_I_00000 is a general error code used for multiple error scenarios
                            // Waiting for backend to provide a specific error code for account deactivation
                            // TODO: Uncomment and update when backend provides specific error code
                            /*
                            if (error?.errorCode === 'SPECIFIC_ACCOUNT_DEACTIVATED_CODE') {
                                // Clean up passkeys for this email if account is deactivated
                                await cleanupPasskeysByEmail(body.email);
                                store.dispatch(Actions.showSignInModal({ isOpen: false }));
                                store.dispatch(
                                    Actions.showAccountDeactivatedModal({
                                        isOpen: true,
                                        errorMessage: error?.errorMessage || getText('passkeyAccountDeactivatedMessage')
                                    })
                                );

                                const deactivatedError = new Error('Account deactivated');
                                deactivatedError.errorCode = 'ACCOUNT_DEACTIVATED';
                                deactivatedError.passkeyFailed = true;

                                return Promise.reject(deactivatedError);
                            }
                            */

                            // Check if this is a revoked passkey error from backend
                            if (isRevokedPasskeyError(error)) {
                                handleRevokedPasskeyError(body.email);

                                const revokedError = new Error('Passkey has been revoked');
                                revokedError.errorCode = 'PASSKEY_REVOKED';
                                revokedError.passkeyFailed = true;

                                return Promise.reject(revokedError);
                            }

                            // Handle all other passkey errors with the same modal (without cleanup)
                            handlePasskeyError(body.email, false);

                            const passkeyError = new Error('Passkey authentication failed');
                            passkeyError.errorCode = 'PASSKEY_FAILED';
                            passkeyError.passkeyFailed = true;

                            return Promise.reject(passkeyError);
                        }

                        // Re-throw other errors (non-passkey login)
                        throw error;
                    })
                    .finally(() => {
                        // Clear flag when login completes (success or error)
                        setAuthOperationInProgress(false);
                    });
            };

            if (!extraParams?.isPasskeyLogin || !extraParams?.passkeyId) {
                return performLoginRequest();
            }

            // Passkey login flow
            try {
                const credentialId = extraParams.passkeyCredentialId;

                // Use SHA-256 digest as state
                const { authenticateUrl, state } = await PasskeyService.generateAuthenticateUrl(body.randomNumber);

                // Authenticate with SDK for the specific passkey
                const authResult = await BeyondIdentityUtils.authenticate(authenticateUrl, credentialId);

                // Parse redirect URL returned by SDK
                const urlParams = PasskeyService.parseRedirectUrl(authResult.redirectUrl);

                // Build API URL for passkey login (dynamic host via ufeApi)
                const queryParams = new URLSearchParams({
                    code: urlParams.code,
                    state: urlParams.state || state,
                    redirectUrl: PasskeyService.getRedirectUri(),
                    identityId: extraParams.identityId,
                    passkeyId: extraParams.passkeyId
                });

                url = `/gway/v1/dotcom/auth/passkeylogin?${queryParams.toString()}`;
            } catch (error) {
                setAuthOperationInProgress(false);

                // Check if this is a revoked passkey error
                if (isRevokedPasskeyError(error)) {
                    handleRevokedPasskeyError(body.email);

                    const revokedError = new Error('Passkey has been revoked');
                    revokedError.errorCode = 'PASSKEY_REVOKED';
                    revokedError.passkeyFailed = true;

                    return Promise.reject(revokedError);
                }

                // Handle all other passkey errors with the same modal (without cleanup)
                handlePasskeyError(body.email, false);

                const passkeyError = new Error('Passkey authentication failed');
                passkeyError.errorCode = 'PASSKEY_FAILED';
                passkeyError.passkeyFailed = true;

                return Promise.reject(passkeyError);
            }

            return performLoginRequest();
        });
    }

    const url = '/api/auth/login';

    return ufeApi
        .makeRequest(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Content-type': 'application/json',
                'x-requested-source': 'web'
            }
        })
        .then(data => (data.errorCode ? Promise.reject(data) : data))
        .finally(() => {
            // Clear flag when login completes (success or error)
            setAuthOperationInProgress(false);
        });
}

export default { login };
